<!-- 
Dialog for creating or editing a scheduled job.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog md">
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>{{ title }}</v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="administration-scheduled-jobs"/>
        </v-toolbar>
        <our-busy-indicator :show="busy"/>
        <v-card-text>
          <v-container grid-list-md pa-0>
            <v-layout wrap>
              <v-flex v-if="state.isNew" xs12>
                <v-select 
                  :items="lookups.jobTypes" :value="scheduledJob.type" :rules="[rules.required]"
                  :autofocus="state.isNew" label="Type*" class="scheduled-job-type" @input="update('type', $event)"
                />
              </v-flex>
              <v-flex xs12>
                <our-date-time-picker 
                  v-if="show"
                  :value="scheduledJob.due" :autofocus="!state.isNew" required
                  label="Due*" class="scheduled-job-due" @input="update('due', $event)"
                />
              </v-flex>
              <v-flex xs12 sm6 md4>
                <v-text-field 
                  :value="scheduledJob.frequency" :rules="[rules.required, rules.integer, rules.greaterThanZero]" class="scheduled-job-frequency"
                  label="Frequency*" required @input="update('frequency', $event)" 
                />
              </v-flex>
              <v-flex xs12 sm6 md4>
                <v-select 
                  :items="lookups.jobFrequencyUnits" :value="scheduledJob.frequencyUnit" :rules="[rules.required]" class="scheduled-job-frequency-unit" 
                  label="Frequency unit*"
                  @input="update('frequencyUnit', $event)"
                  @keydown.enter.native.prevent
                />
              </v-flex>
              <v-flex xs12 sm6 md4>
                <v-checkbox 
                  :input-value="scheduledJob.isSuspended" 
                  class="scheduled-job-is-suspended" label="Is suspended" color="primary"
                  @change="update('isSuspended', $event)" 
                />
              </v-flex>
            </v-layout>
          </v-container>
          <our-required-field-indicator/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="busy" flat @click.native="hide">Cancel</v-btn>
          <v-btn 
            ref="save-button" :disabled="busy" flat 
            color="success" type="submit" @click.prevent="save"
          >Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error" />
  </v-dialog>
</template>

<script>
import * as action from "@/store/scheduled-jobs/action-types";
import * as mutation from "@/store/scheduled-jobs/mutation-types";
import ruleMixins from "@/mixins/rule.mixins";
import lookupMixins from "@/mixins/lookup.mixins";

export default {
  mixins: [ruleMixins, lookupMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    scheduledJobId: {
      type: Number,
      default: null
    },
    create: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      showValidationDialog: false
    };
  },
  computed: {
    state() {
      return this.$store.state.scheduledJobs.edit;
    },
    scheduledJob() {
      return this.state.data;
    },
    busy() {
      return this.state.loading || this.state.saving;
    },
    title() {
      if (this.state.isNew) {
        return "Add scheduled job";
      } else {
        return !this.state.loading
          ? `Edit '${this.lookups.jobTypeDescriptions[this.scheduledJob.type]}' job`
          : "Edit scheduled job";
      }
    }
  },
  watch: {
    show(nowShowing) {
      if (nowShowing) {
        this.$refs.form.reset();
        if (this.create) {
          this.$store.commit(mutation.EDIT_INITIALISE_FOR_CREATE);
        } else {
          this.$store.dispatch(action.EDIT_GET, this.scheduledJobId);
        }
      }
    }
  },
  methods: {
    update(field, newValue) {
      this.$store.commit(mutation.EDIT_SET_DATA, { [field]: newValue });
    },
    async save() {
      if (this.$refs.form.validate() && (await this.$store.dispatch(action.EDIT_SAVE))) {
        this.hide();
        if (this.create) {
          this.$router.push(`/scheduled-jobs/${this.scheduledJob.id}`);
        }
      } else {
        this.showValidationDialog = true;
      }
    },
    hide() {
      this.$emit("update:show", false);
    }
  }
};
</script>
